import { ref, computed } from 'vue'

type ThemeMode = 'light' | 'dark' | 'system'

// Internal reactive state for theme mode
const _themeMode = ref<ThemeMode>('system')
const _isDark = ref<boolean>(false)

// Initialize on first use
let initialized = false
let mediaQuery: MediaQueryList | null = null

const initializeTheme = () => {
  if (initialized || typeof window === 'undefined') return

  // Check if we have a saved preference, otherwise use system preference
  const savedTheme = localStorage.getItem('theme-mode') as ThemeMode

  // Set initial theme mode
  if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
    _themeMode.value = savedTheme
  } else {
    _themeMode.value = 'system'
  }

  // Set up media query listener for system preference changes
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.addEventListener('change', handleSystemPreferenceChange)

  // Apply initial state
  applyThemeMode(_themeMode.value)

  initialized = true
}

const handleSystemPreferenceChange = (event: MediaQueryListEvent) => {
  if (_themeMode.value === 'system') {
    _isDark.value = event.matches
    document.documentElement.classList.toggle('dark', event.matches)
  }
}

const applyThemeMode = (mode: ThemeMode) => {
  try {
    _themeMode.value = mode
    
    let shouldBeDark = false
    
    switch (mode) {
      case 'light':
        shouldBeDark = false
        break
      case 'dark':
        shouldBeDark = true
        break
      case 'system':
        shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        break
    }
    
    _isDark.value = shouldBeDark
    
    // Remove any existing dark class first
    document.documentElement.classList.remove('dark')
    // Add dark class if needed
    if (shouldBeDark) {
      document.documentElement.classList.add('dark')
    }
    
    localStorage.setItem('theme-mode', mode)
  } catch (error) {
    console.error('Error applying theme mode:', error)
  }
}

const cycleThemeMode = () => {
  const modes: ThemeMode[] = ['light', 'dark', 'system']
  const currentIndex = modes.indexOf(_themeMode.value)
  const nextIndex = (currentIndex + 1) % modes.length
  applyThemeMode(modes[nextIndex])
}

export function useDarkMode() {
  // Initialize on first call
  initializeTheme()

  // Computed property for two-way binding with reactive getter/setter
  const isDark = computed({
    get: () => _isDark.value,
    set: (value: boolean) => {
      const mode: ThemeMode = value ? 'dark' : 'light'
      applyThemeMode(mode)
    },
  })

  const themeMode = computed({
    get: () => _themeMode.value,
    set: (value: ThemeMode) => applyThemeMode(value),
  })

  return {
    isDark,
    themeMode,
    cycleThemeMode,
  }
}
